// @ts-nocheck
import React, { useContext, useState } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthContext, useAuth } from "../../lib/AuthContext";
import XanoAPI from "../../lib/XanoAPI";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { getStates } from "../../lib/utils";
import { UserPlus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../components/ui/form";
// import { ClientType } from "../../types";

// Singleton API instance
const api = XanoAPI.getInstance();

// Define the form validation schema
const formSchema = z.object({
  user_id: z.number().optional(),
  firstName: z
    .string()
    .min(2, "First Name must be at least 2 characters.")
    .nonempty("First Name is required"),
  lastName: z
    .string()
    .min(2, "Last Name must be at least 2 characters.")
    .nonempty("Last Name is required"),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits long.")
    .regex(/^\d+$/, "Phone number should only contain digits")
    .nonempty("Phone number is required"),
  stateProvince: z
    .string()
    .min(2, "Please provide a valid state or province")
    .nonempty("State/Province is required"),
  type: z.enum(["federal", "401k", "403b"]),
});

// Infer the type from the schema
type FormData = z.infer<typeof formSchema>;

export function AddClientModal() {
  const auth = useAuth();
  const flags = auth.featureFlags;
  const [enabled401kPlan, isEnabled401kPlan] = useState(
    auth?.currentUser?.module_401k_plan
  );
  const [enabled403bPlan, isEnabled403bPlan] = useState(
    auth?.currentUser?.module_403b_plan
  );

  const isAllowed = (flag: string) => flags.includes(flag);
  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      user_id: 0,
      firstName: "",
      lastName: "",
      phoneNumber: "",
      stateProvince: "",
      type: "federal",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = methods;

  const onSubmit = async (data: FormData) => {
    const userId = auth.currentUser?.id || api.getUserId();

    if (userId === 0) {
      console.error("Invalid user ID. Could Not Submit the form");
      return;
    }

    const clientData: ClientType = {
      ...data,
      user_id: userId,
    };

    console.log(clientData);

    try {
      const newClient = await api.addClient(clientData);
      if (newClient.id) window.location.replace(`/edit-client/${newClient.id}`);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          size="sm"
          className="bg-accent hover:bg-accent transition-all duration-200 add-client-button"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Client
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px] add-client-modal">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Personal Information</DialogTitle>
              <DialogDescription>
                Add the basic client information here before you can continue.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 gap-4">
                <Label htmlFor="firstName" className="text-left">
                  First Name
                </Label>
                <FormControl>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          id="firstName"
                          placeholder="Client's first name ..."
                          className="w-full"
                        />
                        {error && (
                          <FormMessage className="text-red-500 font-normal text-xs">
                            {error.message}
                          </FormMessage>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Label htmlFor="lastName" className="text-left">
                  Last Name
                </Label>
                <FormControl>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          id="lastName"
                          placeholder="Client's last name ..."
                          className="w-full"
                        />
                        {error && (
                          <FormMessage className="text-red-500 font-normal text-xs">
                            {error.message}
                          </FormMessage>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Label htmlFor="phoneNumber" className="text-left">
                  Phone Number
                </Label>
                <FormControl>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          id="phoneNumber"
                          placeholder="Client's phone number ..."
                          className="w-full"
                        />
                        {error && (
                          <FormMessage className="text-red-500 font-normal text-xs">
                            {error.message}
                          </FormMessage>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              <div className="grid grid-cols-1 gap-4 field-select-state">
                <Label htmlFor="stateProvince" className="text-left">
                  State / Province
                </Label>
                <FormControl>
                  <Controller
                    name="stateProvince"
                    control={control}
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <Select
                          onValueChange={(val) => onChange(val)}
                          value={value}
                        >
                          <SelectTrigger className="w-full select-state">
                            <SelectValue placeholder={`Select State`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {getStates()
                                .filter((option) => option.value !== "")
                                .map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {error && (
                          <FormMessage className="text-red-500 font-normal text-xs">
                            {error.message}
                          </FormMessage>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              <div className="grid grid-cols-1 gap-4 field-client-type">
                <Label htmlFor="type" className="text-left">
                  Type
                </Label>
                <FormControl>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={(val) => field.onChange(val)}
                        value={field.value}
                      >
                        <SelectTrigger className="w-full select-type">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="federal">Federal</SelectItem>
                            {isAllowed("401k") && enabled401kPlan && (
                              <SelectItem value="401k">401k</SelectItem>
                            )}
                            {isAllowed("403b") && enabled403bPlan && (
                              <SelectItem value="403b">403b</SelectItem>
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormControl>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                type="submit" 
                disabled={!isValid}
                className="hover:bg-accent hover:text-accent-foreground hover:scale-105 transition-all duration-200 disabled:hover:scale-100"
              >
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
